import { useCallback, useMemo, useRef, useEffect, Fragment } from 'react';
import { makeGetColumnWidth, setWidth } from '../../resizing/redux/widthsSlice';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import { ContentComponent } from './getContentComponent';
import { getColumnKey } from '../../table/utils/getColumnKey';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useFDTContextMenuConfig } from '../../contextMenu/context/FDTContextMenuConfigContext';
import { SortTH } from 'foundations-components/transitional/components/Table';
import { DragHandleHoverContainer } from 'foundations-components/transitional/components/DragHandleHoverContainer';
import { DragHandle } from 'foundations-components/transitional/components/DragHandle';
import { jsx as _jsx } from "react/jsx-runtime";
const getSortFromDirection = sortDirection => {
  if (sortDirection === -1) {
    return 'ascending';
  }
  if (sortDirection === 1) {
    return 'descending';
  }
  return 'none';
};
const getDirectionFromSort = sort => sort === 'ascending' ? -1 : 1;
const ResizableSortTH = ({
  column,
  sortDirection,
  columnIndex,
  onColumnWidthChange,
  onColumnOrderChange,
  onSortChange,
  children
}) => {
  const {
    columnName,
    columnNamespace,
    sortable,
    width: columnWidth,
    minimumColumnWidth,
    reorderable
  } = column;
  const columnKey = getColumnKey(columnNamespace, columnName);
  const getColumnWidth = useMemo(() => makeGetColumnWidth(columnKey), [columnKey]);
  const width = useTableSelector(getColumnWidth);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  // We must use a ref in this callback because handleMouseMove in getContent above
  // captures the first version it sees. We keep this ref in sync with component
  // state via useEffect. This is when we "commit" the new width.
  const onResizeEnd = useCallback(() => {
    onColumnWidthChange({
      columnNamespace,
      columnName,
      nextWidth: widthRef.current
    });
  }, [columnNamespace, columnName, onColumnWidthChange]);
  const dispatch = useTableDispatch();
  const handleResize = useCallback(nextWidth => dispatch(setWidth({
    columnKey,
    width: Math.max(nextWidth, minimumColumnWidth)
  })), [columnKey, dispatch, minimumColumnWidth]);
  const handleSortChange = useCallback(({
    target: {
      value
    }
  }) => onSortChange({
    columnNamespace,
    columnName,
    direction: getDirectionFromSort(value)
  }), [onSortChange, columnName, columnNamespace]);
  const Content = useCallback(({
    arrowsNode,
    children: contentChildren
  }) => /*#__PURE__*/_jsx(ContentComponent, {
    arrowsNode: arrowsNode,
    column: column,
    originalWidth: columnWidth,
    columnIndex: columnIndex,
    handleResize: handleResize,
    onResizeEnd: onResizeEnd,
    onColumnOrderChange: onColumnOrderChange,
    children: contentChildren
  }), [column, columnWidth, columnIndex, handleResize, onResizeEnd, onColumnOrderChange]);

  // This id is part of our external api and should not be changed.
  // It will be used for features like coaching tips and onboarding tours.
  const externalId = `header-${columnNamespace}-${columnName}`;

  // HACK: These selectors are required for compatibility with onboarding tours
  // and coaching tips. When we've renamed the contact "name" column to it's
  // more permanent placeholder name, we should remove this and PR onboarding
  // tours/coaching tips to use the standardaized selectors.
  const isContact = columnNamespace === CONTACT_TYPE_ID;
  const coachingTipsSelectors = isContact && columnName === 'name' ? {
    'data-coaching-tips': 'header-name'
  } : {};
  const onboardingSelectors = isContact ? {
    'data-onboarding': `crm-table-header-cell-${columnName}`
  } : {};
  const contextMenuConfig = useFDTContextMenuConfig();
  const triggerContextMenu = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.triggerContextMenu;
  const handleContextMenu = useMemo(() => {
    return triggerContextMenu ? e => {
      e.preventDefault();
      e.stopPropagation();
      triggerContextMenu({
        row: null,
        columnNamespace,
        columnName
      });
    } : undefined;
  }, [triggerContextMenu, columnNamespace, columnName]);
  return /*#__PURE__*/_jsx(ResizableSortTHWrapper, {
    reorderable: reorderable,
    children: /*#__PURE__*/_jsx(SortTH, Object.assign({}, coachingTipsSelectors, onboardingSelectors, {
      "data-column-index": columnIndex
      // HACK: We should just be writing tests against the redux selector — reading it through the table cell
      // adds an unnecessary level of indirection that broke with the switch to inlineSize.
      ,
      "data-test-width": width,
      inlineSize: `${width}px`,
      padding: "0",
      role: "columnheader",
      "data-table-external-id": externalId,
      "data-test-id": externalId,
      "data-test-sort-direction": sortDirection,
      Content: Content,
      FlexContainer: Fragment,
      disabled: !sortable,
      "aria-disabled": !sortable,
      sort: getSortFromDirection(sortDirection),
      onSortChange: handleSortChange
      // HACK: We should see whether we can add this prop to SortTH — I don't like relying on this
      // @ts-expect-error this is technically a valid DOM prop
      ,
      onContextMenu: handleContextMenu,
      children: children
    }))
  });
};
function ResizableSortTHWrapper({
  children,
  reorderable
}) {
  return reorderable ? /*#__PURE__*/_jsx(DragHandleHoverContainer, {
    children: /*#__PURE__*/_jsx(DragHandle, {
      children: children
    })
  }) : children;
}
export default ResizableSortTH;